import { useQueryClient } from '@tanstack/react-query'
import { IS_MOCK } from './mockMode'
import { useUi } from './ui'

// ─── Precise location: the tap ("Use precise location") ─────────────────────

/**
 * Browser geolocation on explicit user action; the result becomes My Location (precise).
 * Progress and any error are app-wide (every "use my location" control shows the same state;
 * the error appears once, as a toast — see LocateToast in App). The implementation, and
 * the silent permission-aware following, live in myLocation.ts (its own chunk).
 */
export function useLocate() {
  const busy = useUi((s) => s.locating)
  const client = useQueryClient()
  const locate = () => {
    useUi.getState().setLocate(true, null)
    import('./myLocation')
      .then((m) => m.locateNow(client))
      .catch(() => useUi.getState().setLocate(false, 'Couldn’t start location. Check your connection and try again.'))
  }
  return { busy, locate }
}

// ─── First visit: the visitor's approximate area from /api/locate ───────────

/** GET /api/locate (web/functions/api/locate.ts): coarse, from Cloudflare's own request data. */
export type LocateResult =
  | {
      ok: true
      approximate: true
      latitude: number
      longitude: number
      city: string | null
      region: string | null
      regionCode: string | null
      country: string | null
      timezone: string | null
      inCoverage: boolean
    }
  | { ok: false; reason: string }

export type FirstVisitNotice = { kind: 'outside'; country: string | null } | { kind: 'unavailable' } | null

const STORE_KEY = 'wf2-app'

/** A first visit: nothing saved yet, no place in the link, not demo mode. */
export function isFirstVisit(search = location.search, pathname = location.pathname): boolean {
  if (IS_MOCK || pathname !== '/') return false
  const q = new URLSearchParams(search)
  if (q.get('lat') && q.get('lon')) return false
  try {
    return localStorage.getItem(STORE_KEY) == null
  } catch {
    return true // no storage: every visit starts fresh
  }
}
